'use client';
import { TypeGlobalBannerFields } from '@/core/generated-types';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { RichText } from '@/components/';
import { Document } from '@contentful/rich-text-types';
import classNames from 'classnames';
import { useSearchParams } from 'next/navigation';
import useAuth from '@/context/AuthContext';
import styles from './GlobalBanner.module.scss';
import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  PAGE_LOCATIONS,
  trackItemClickedEvent,
} from '@/utils/rudderstack';

const GlobalBanner: FC<TypeGlobalBannerFields> = ({
  userLoggedOutText,
  userLoggedInText,
  showAccessibilityLink = false,
  enableAccessibilityLabel = 'Enable Accessibility',
  disableAccessibilityLabel = 'Disable Accessibility',
  backgroundColor,
  textColor,
}) => {
  const [hydrated, setHydrated] = useState(false);

  const searchParams = useSearchParams();
  const a11yEnabled = searchParams?.get('UsableNetAssistive') === 'true';

  const { user } = useAuth();
  const useLoggedInText = !!user && userLoggedInText;
  const textToDisplay = useLoggedInText ? userLoggedInText : userLoggedOutText;

  useEffect(() => {
    setHydrated(true);
  }, []);

  //  US global banner have a different color than other stores
  const isUSStore = process.env.NEXT_PUBLIC_REGION === 'US';

  return (
    <div
      data-testid="global-banner"
      className={classNames(
        `relative z-10 px-3 py-[10px] md:text-base text-sm`,
        backgroundColor
          ? `bg-${backgroundColor.fields?.color}`
          : isUSStore
            ? 'bg-blue-deep'
            : 'bg-aqua',
        textColor
          ? `text-${textColor.fields?.color}`
          : isUSStore
            ? 'text-white'
            : ``,
      )}
    >
      <div
        className={classNames(
          showAccessibilityLink
            ? 'w-[60%] text-left md:w-full md:text-center'
            : 'w-full text-center',
          styles.bannerStyle,
        )}
      >
        {textToDisplay && hydrated && (
          <RichText
            openLinkSameWindow={true}
            content={textToDisplay as Document}
          />
        )}
      </div>

      {showAccessibilityLink && (
        <div className="absolute right-3 top-1/2 w-[100px] -translate-y-1/2 transform text-right md:w-auto">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            className="UsableNetAssistive font-bold underline"
            onClick={(e) => {
              e.preventDefault();
              trackItemClickedEvent({
                type: CLICKED_ITEM_TYPES.LINK,
                cta: a11yEnabled
                  ? CTA_NAMES.EXIT_ACCESSIBILITY
                  : CTA_NAMES.ENABLE_ACCESSIBILITY,
                pageLocation: PAGE_LOCATIONS.SITE_BANNER,
                url: window.location.href,
              });
              (window as any)?.enableUsableNetAssistive();
            }}
            href="#"
          >
            {a11yEnabled ? disableAccessibilityLabel : enableAccessibilityLabel}
          </a>
        </div>
      )}
    </div>
  );
};

export default React.memo(GlobalBanner);
